import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  AsYouType,
  isPossiblePhoneNumber,
  getExampleNumber,
  parsePhoneNumberFromString,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import type { CountryRate } from '@/types/pricing'

// ---------------------------------------------------------------------------
// Country option types
// ---------------------------------------------------------------------------

/**
 * A minimal country descriptor used by this hook internally.
 * It can be populated from pricing data (dialer path) or from libphonenumber-js
 * metadata (form path, when only ISO codes are known).
 */
export interface PhoneCountryOption {
  countryCode: string
  dialingCode: string // e.g. '+44'
  /** Display name, used by the dialer's CountrySelector */
  country?: string
  /** Whether to prefer this country when multiple share a dialing code */
  dialingCodePreferred?: boolean
  /** Pass-through of any additional pricing/display data from the pricing API */
  meta?: CountryRate
}

const countryDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' })

/** Build a PhoneCountryOption from an ISO code using libphonenumber-js metadata. */
function countryOptionFromCode(code: string): PhoneCountryOption | null {
  if (!isSupportedCountry(code)) return null
  return {
    countryCode: code,
    dialingCode: `+${getCountryCallingCode(code as CountryCode)}`,
    country: countryDisplayNames.of(code) ?? code,
  }
}

/** Convert a pricing CountryRate into a PhoneCountryOption. */
function countryOptionFromRate(rate: CountryRate): PhoneCountryOption {
  return {
    countryCode: rate.countryCode,
    dialingCode: rate.dialingCode,
    country: rate.country,
    dialingCodePreferred: rate.dialingCodePreferred,
    meta: rate,
  }
}

// ---------------------------------------------------------------------------
// Hook options
// ---------------------------------------------------------------------------

export interface UsePhoneInputOptions {
  /**
   * Full country list from the pricing API. When provided the country selector
   * shows all callable countries (dialer use-case).
   * Mutually exclusive with `allowedCountryCodes`.
   */
  pricingCountries?: CountryRate[]
  /**
   * ISO 3166-1 alpha-2 codes to restrict the country selector to. Country
   * metadata (dialing code, name) is derived from libphonenumber-js.
   * Used by form inputs where the backend enforces a country allowlist.
   */
  allowedCountryCodes?: string[]
  /** Initial phone number value (E.164 or national format). */
  initialValue?: string
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function rawDigits(value: string): string {
  // Strip all formatting except leading + and DTMF chars * #
  let cleaned = value.replace(/[^\d+*#]/g, '')
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '')
  } else {
    cleaned = cleaned.replace(/\+/g, '')
  }
  return cleaned
}

function formatWith(raw: string, country: PhoneCountryOption | null): string {
  try {
    const formatter = new AsYouType(country?.countryCode as CountryCode | undefined)
    return formatter.input(raw) || raw
  } catch {
    return raw
  }
}

function e164From(raw: string, country: PhoneCountryOption | null): string {
  try {
    const formatter = new AsYouType(country?.countryCode as CountryCode | undefined)
    formatter.input(raw)
    const val = formatter.getNumberValue()
    if (val) return val
  } catch { /* fall through */ }

  // Fallback: try parsePhoneNumberFromString with the country context
  if (country?.countryCode) {
    try {
      const parsed = parsePhoneNumberFromString(raw, country.countryCode as CountryCode)
      if (parsed) return parsed.format('E.164')
    } catch { /* ignore */ }
  }
  return ''
}

function detectCountryFromValue(
  value: string,
  options: PhoneCountryOption[],
): PhoneCountryOption | null {
  if (!value.startsWith('+')) return null
  const sorted = [...options].sort((a, b) => b.dialingCode.length - a.dialingCode.length)
  let bestLen = 0
  const matches: PhoneCountryOption[] = []
  for (const opt of sorted) {
    if (value.startsWith(opt.dialingCode)) {
      if (opt.dialingCode.length > bestLen) {
        bestLen = opt.dialingCode.length
        matches.length = 0
      }
      matches.push(opt)
    }
  }
  if (matches.length === 0) return null
  if (matches.length === 1) return matches[0]
  return matches.find((c) => c.dialingCodePreferred) ?? matches[0]
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UsePhoneInputResult {
  // --- display state ---
  /** Formatted display value (e.g. "07700 900000" or "+44 7700 900000") */
  displayValue: string
  /** E.164 value (e.g. "+447700900000"), or empty string if incomplete */
  e164: string
  /** True when the number is a structurally possible phone number */
  isValid: boolean
  /** Example national number for the selected country (for placeholder) */
  exampleNumber: string

  // --- country state ---
  selectedCountry: PhoneCountryOption | null
  /** Full list of selectable countries (filtered/all depending on options) */
  countryOptions: PhoneCountryOption[]
  setCountry: (country: PhoneCountryOption) => void
  /** Detect and return the matching country for a given number string */
  detectCountry: (value: string) => PhoneCountryOption | null

  // --- imperative actions (keypad / keyboard / clipboard) ---
  /** Append a single character (digit, +, *, #) */
  handleDigit: (char: string) => void
  handleBackspace: () => void
  handleClear: () => void
  /** Set the full value from a paste or external source (contact, URL param) */
  handlePaste: (raw: string) => void
  /**
   * Directly set a full value string (E.164 or national). Auto-detects country.
   * Useful for controlled-input onChange and programmatic updates.
   */
  setValue: (value: string) => void
}

export function usePhoneInput({
  pricingCountries,
  allowedCountryCodes,
  initialValue = '',
}: UsePhoneInputOptions = {}): UsePhoneInputResult {
  // Build the country options list once per input change
  const countryOptions = useMemo<PhoneCountryOption[]>(() => {
    if (pricingCountries?.length) {
      return pricingCountries.map(countryOptionFromRate)
    }
    if (allowedCountryCodes?.length) {
      return allowedCountryCodes
        .map(countryOptionFromCode)
        .filter((c): c is PhoneCountryOption => c !== null)
        .sort((a, b) => (a.country ?? '').localeCompare(b.country ?? ''))
    }
    return []
  }, [pricingCountries, allowedCountryCodes])

  const [selectedCountry, setSelectedCountry] = useState<PhoneCountryOption | null>(() => {
    if (!initialValue) return null
    return detectCountryFromValue(initialValue, countryOptions) ?? null
  })

  const [displayValue, setDisplayValue] = useState<string>(() => {
    if (!initialValue) return ''
    return formatWith(initialValue, selectedCountry)
  })

  useEffect(() => {
    setSelectedCountry((current) => {
      const detected = initialValue
        ? (detectCountryFromValue(rawDigits(initialValue), countryOptions) ?? null)
        : null

      if (detected) {
        return current?.countryCode === detected.countryCode ? current : detected
      }

      if (current && countryOptions.some((option) => option.countryCode === current.countryCode)) {
        return current
      }

      return null
    })
  }, [countryOptions, initialValue])

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------

  const e164 = useMemo(
    () => e164From(displayValue, selectedCountry),
    [displayValue, selectedCountry],
  )

  const isValid = useMemo(() => {
    if (!displayValue) return false
    try {
      return isPossiblePhoneNumber(displayValue, selectedCountry?.countryCode as CountryCode | undefined)
    } catch {
      return false
    }
  }, [displayValue, selectedCountry])

  const exampleNumber = useMemo(() => {
    if (!selectedCountry?.countryCode) return ''
    try {
      const ex = getExampleNumber(selectedCountry.countryCode as CountryCode, examples)
      return ex?.formatNational() ?? ''
    } catch {
      return ''
    }
  }, [selectedCountry?.countryCode])

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const detectCountry = useCallback(
    (value: string) => detectCountryFromValue(value, countryOptions),
    [countryOptions],
  )

  const setCountry = useCallback((country: PhoneCountryOption) => {
    setSelectedCountry(country)
  }, [])

  /** Internal: reformat a raw string and update state. Also auto-detects country. */
  const applyValue = useCallback(
    (raw: string, forceCountry?: PhoneCountryOption | null) => {
      const country = forceCountry !== undefined
        ? forceCountry
        : (raw.startsWith('+') ? (detectCountryFromValue(raw, countryOptions) ?? selectedCountry) : selectedCountry)

      if (country !== selectedCountry) setSelectedCountry(country)
      setDisplayValue(formatWith(raw, country))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countryOptions, selectedCountry],
  )

  const handleDigit = useCallback(
    (char: string) => {
      setDisplayValue((prev) => {
        let next: string
        if (char === '+') {
          // Replace everything with '+', like a standard dialer
          next = prev.startsWith('+') ? prev : '+'
        } else {
          const prevRaw = rawDigits(prev)
          next = prevRaw + char
        }
        const country = next.startsWith('+')
          ? (detectCountryFromValue(next, countryOptions) ?? selectedCountry)
          : selectedCountry
        if (country !== selectedCountry) setSelectedCountry(country)
        return formatWith(next, country)
      })
    },
    [countryOptions, selectedCountry],
  )

  const handleBackspace = useCallback(() => {
    setDisplayValue((prev) => {
      const raw = rawDigits(prev)
      const trimmed = raw.slice(0, -1)
      return formatWith(trimmed, selectedCountry)
    })
  }, [selectedCountry])

  const handleClear = useCallback(() => {
    setDisplayValue('')
  }, [])

  const handlePaste = useCallback(
    (raw: string) => {
      if (!raw) return
      const cleaned = rawDigits(raw)
      if (!cleaned) return
      applyValue(cleaned)
    },
    [applyValue],
  )

  const setValue = useCallback(
    (value: string) => {
      if (!value) {
        setDisplayValue('')
        return
      }
      applyValue(rawDigits(value))
    },
    [applyValue],
  )

  return {
    displayValue,
    e164,
    isValid,
    exampleNumber,
    selectedCountry,
    countryOptions,
    setCountry,
    detectCountry,
    handleDigit,
    handleBackspace,
    handleClear,
    handlePaste,
    setValue,
  }
}
